import { createSignal, type Accessor } from 'solid-js';
import { asError } from '@trimble/logging';
import { useLogger } from '@trimble/solidjs-utils';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { t } from '@trimble/i18n';

import type {
  FxBreakline,
  FxBreaklineType,
} from './fx-workspace-breakline.types';
import type { FxWorkspaceRepository } from './fx-workspace-repository';
import {
  createRepositoryScopeGuard,
  createWorkspaceScopeGuard,
} from './fx-workspace-scope';

export interface FxWorkspaceBreaklineActionsApi {
  isBreaklineCreating(workspaceId: string, type: FxBreaklineType): boolean;
  isBreaklinesBuilding(workspaceId: string): boolean;
  createBreakline(workspaceId: string, type: FxBreaklineType): Promise<void>;
  runBreaklinesExtraction(
    workspaceId: string,
    types: readonly FxBreaklineType[]
  ): Promise<void>;
  publishBreakline(workspaceId: string, type: FxBreaklineType): void;
  publishAllBreaklines(workspaceId: string): void;
  clearWorkspace(workspaceId: string): void;
  resetRepositoryScope(): void;
}

const isBreaklineAlreadyExistsError = (error: unknown): boolean =>
  asError(error).message.includes('409');

const breaklineKey = (workspaceId: string, type: FxBreaklineType): string =>
  `${workspaceId}::${type}`;

export function useFxWorkspaceBreaklineActions(options: {
  repository: Accessor<FxWorkspaceRepository>;
  getBreakline(
    workspaceId: string,
    type: FxBreaklineType
  ): FxBreakline | undefined;
  appendWorkspaceBreakline(workspaceId: string, breakline: FxBreakline): void;
  reloadWorkspaceBreaklines(workspaceId: string): Promise<void>;
}): FxWorkspaceBreaklineActionsApi {
  const logger = useLogger().getLogger('FxWorkspace');
  const scope = createRepositoryScopeGuard();
  const workspaceScope = createWorkspaceScopeGuard();

  const [creatingBreaklineKeys, setCreatingBreaklineKeys] = createSignal<
    ReadonlySet<string>
  >(new Set());
  const [breaklinesBuildingWorkspaceIds, setBreaklinesBuildingWorkspaceIds] =
    createSignal<ReadonlySet<string>>(new Set());

  const isBreaklineCreating = (
    workspaceId: string,
    type: FxBreaklineType
  ): boolean => {
    return creatingBreaklineKeys().has(breaklineKey(workspaceId, type));
  };

  const isBreaklinesBuilding = (workspaceId: string): boolean =>
    breaklinesBuildingWorkspaceIds().has(workspaceId);

  const createBreakline = async (
    workspaceId: string,
    type: FxBreaklineType
  ): Promise<void> => {
    if (options.getBreakline(workspaceId, type) !== undefined) {
      return;
    }

    const key = breaklineKey(workspaceId, type);
    const scopeSnapshot = scope.capture();
    const workspaceSnapshot = workspaceScope.capture(workspaceId);
    const isCreateCurrent = (): boolean =>
      scope.isCurrent(scopeSnapshot) &&
      workspaceScope.isCurrent(workspaceId, workspaceSnapshot);
    setCreatingBreaklineKeys((previous) => new Set<string>([...previous, key]));

    try {
      const created = await options
        .repository()
        .createBreakline(
          workspaceId,
          type,
          t(`mobileMapping.fxWorkspace.breaklines.defaultName.${type}`)
        );
      if (!isCreateCurrent()) {
        return;
      }

      options.appendWorkspaceBreakline(workspaceId, created);
    } catch (error: unknown) {
      if (!isCreateCurrent()) {
        return;
      }

      const err = asError(error);
      logger.error(
        'Failed to create FX workspace breakline',
        { workspaceId, type },
        err
      );
      NotificationEventBus.emit({
        type: 'error',
        message: isBreaklineAlreadyExistsError(error)
          ? t('mobileMapping.fxWorkspace.breaklines.createFailedAlreadyExists')
          : t('mobileMapping.fxWorkspace.breaklines.createFailed'),
      });
    } finally {
      if (isCreateCurrent()) {
        setCreatingBreaklineKeys((previous) => {
          const next = new Set(previous);
          next.delete(key);
          return next;
        });
      }
    }
  };

  const runBreaklinesExtraction = async (
    workspaceId: string,
    types: readonly FxBreaklineType[]
    // eslint-disable-next-line sonarjs/cognitive-complexity -- SonarJS trial (NUMARK-1243): complexity 20 > 15 allowed; flagged for later refactor, not fixed here
  ): Promise<void> => {
    const typesToCreate = types.filter(
      (type) => options.getBreakline(workspaceId, type) === undefined
    );
    if (typesToCreate.length === 0) {
      return;
    }

    const scopeSnapshot = scope.capture();
    const workspaceSnapshot = workspaceScope.capture(workspaceId);
    const isExtractionCurrent = (): boolean =>
      scope.isCurrent(scopeSnapshot) &&
      workspaceScope.isCurrent(workspaceId, workspaceSnapshot);
    const activeRepository = options.repository();

    setBreaklinesBuildingWorkspaceIds(
      (previous) => new Set<string>([...previous, workspaceId])
    );

    let failureCount = 0;

    try {
      for (const type of typesToCreate) {
        if (!isExtractionCurrent()) {
          return;
        }

        const key = breaklineKey(workspaceId, type);
        setCreatingBreaklineKeys(
          (previous) => new Set<string>([...previous, key])
        );

        try {
          const created = await activeRepository.createBreakline(
            workspaceId,
            type,
            t(`mobileMapping.fxWorkspace.breaklines.defaultName.${type}`)
          );
          if (!isExtractionCurrent()) {
            return;
          }

          options.appendWorkspaceBreakline(workspaceId, created);
        } catch (error: unknown) {
          if (!isExtractionCurrent()) {
            return;
          }

          if (isBreaklineAlreadyExistsError(error)) {
            continue;
          }

          failureCount += 1;
          logger.error(
            'Failed to create FX workspace breakline during extraction',
            { workspaceId, type },
            asError(error)
          );
        } finally {
          if (isExtractionCurrent()) {
            setCreatingBreaklineKeys((previous) => {
              const next = new Set(previous);
              next.delete(key);
              return next;
            });
          }
        }
      }

      if (!isExtractionCurrent()) {
        return;
      }

      await options.reloadWorkspaceBreaklines(workspaceId);

      if (!isExtractionCurrent()) {
        return;
      }

      if (failureCount > 0) {
        NotificationEventBus.emit({
          type: 'error',
          message: t(
            'mobileMapping.fxWorkspace.breaklines.createFailedPartial'
          ),
        });
      }
    } finally {
      if (isExtractionCurrent()) {
        setBreaklinesBuildingWorkspaceIds((previous) => {
          const next = new Set(previous);
          next.delete(workspaceId);
          return next;
        });
      }
    }
  };

  const publishBreakline = (
    _workspaceId: string,
    _type: FxBreaklineType
  ): void => {
    // Phase 7 stub — real publish API in Phase 12+
  };

  const publishAllBreaklines = (_workspaceId: string): void => {
    // Phase 7 stub — real publish API in Phase 12+
  };

  const clearWorkspace = (workspaceId: string): void => {
    workspaceScope.invalidate(workspaceId);
    setCreatingBreaklineKeys((previous) => {
      const next = new Set(previous);
      for (const key of next) {
        if (key.startsWith(`${workspaceId}::`)) {
          next.delete(key);
        }
      }
      return next;
    });
    setBreaklinesBuildingWorkspaceIds((previous) => {
      const next = new Set(previous);
      next.delete(workspaceId);
      return next;
    });
  };

  return {
    isBreaklineCreating,
    isBreaklinesBuilding,
    createBreakline,
    runBreaklinesExtraction,
    publishBreakline,
    publishAllBreaklines,
    clearWorkspace,
    resetRepositoryScope: (): void => {
      scope.invalidate();
      workspaceScope.clear();
      setCreatingBreaklineKeys(new Set<string>());
      setBreaklinesBuildingWorkspaceIds(new Set<string>());
    },
  };
}
